import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, Calendar,
  Clock, MapPin, Tag, Edit, X, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'class', label: 'Class / Lesson', color: 'bg-emerald-500' },
  { value: 'workshop', label: 'Workshop', color: 'bg-purple-500' },
  { value: 'event', label: 'Event', color: 'bg-amber-500' },
  { value: 'webinar', label: 'Webinar', color: 'bg-sky-500' },
  { value: 'milestone', label: 'Milestone', color: 'bg-rose-500' },
  { value: 'theme', label: 'Monthly Theme', color: 'bg-indigo-500' },
];

const TYPES = [
  { value: 'monthly', label: 'Monthly Schedule' },
  { value: 'yearly', label: 'Yearly Activity' },
];

const emptyItem = {
  title: '', date: '', time: '', location_link: '',
  category: 'event', description: '', item_type: 'monthly',
};

const ScheduleManager = ({ user }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyItem });
  const [isNew, setIsNew] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const fetchItems = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/schedule`, { credentials: 'include' });
      if (res.ok) setItems(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSave = async () => {
    if (!form.title || !form.date) return;
    setSaving(true);
    try {
      const url = isNew
        ? `${BACKEND_URL}/api/admin/schedule`
        : `${BACKEND_URL}/api/admin/schedule/${editingId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        await fetchItems();
        setEditingId(null);
        setIsNew(false);
        setForm({ ...emptyItem });
      }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this schedule item?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/schedule/${itemId}`, {
        method: 'DELETE', credentials: 'include',
      });
      await fetchItems();
    } catch (e) { console.error(e); }
  };

  const startEdit = (item) => {
    setEditingId(item.item_id);
    setIsNew(false);
    setForm({
      title: item.title, date: item.date, time: item.time || '',
      location_link: item.location_link || '', category: item.category || 'event',
      description: item.description || '', item_type: item.item_type || 'monthly',
    });
  };

  const startNew = () => {
    setEditingId('new');
    setIsNew(true);
    setForm({ ...emptyItem });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsNew(false);
    setForm({ ...emptyItem });
  };

  const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[2];
  const filtered = filterType === 'all' ? items : items.filter(i => i.item_type === filterType);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="text-gray-400 hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Schedule & Activities</h1>
              <p className="text-gray-500 text-sm">Manage monthly schedules and yearly activities</p>
            </div>
          </div>
          <Button onClick={startNew} className="bg-amber-600 hover:bg-amber-500" data-testid="add-schedule-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {[{ value: 'all', label: 'All' }, ...TYPES].map(t => (
            <button
              key={t.value}
              onClick={() => setFilterType(t.value)}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                filterType === t.value
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid={`filter-${t.value}`}
            >
              {t.label}
            </button>
          ))}
          <span className="ml-auto text-sm text-gray-500 self-center">{filtered.length} items</span>
        </div>

        {/* New/Edit Form */}
        {editingId && (
          <Card className="bg-[#141414] border-amber-500/30 mb-6" data-testid="schedule-form">
            <CardContent className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-white font-semibold">{isNew ? 'New Schedule Item' : 'Edit Item'}</h3>
                <Button variant="ghost" size="sm" onClick={cancelEdit} className="text-gray-400 hover:text-white">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">Title *</Label>
                  <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. BaZi Beginner Class" className="bg-gray-800 border-gray-700 text-white mt-1"
                    data-testid="form-title" />
                </div>
                <div>
                  <Label className="text-gray-400">Date *</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="form-date" />
                </div>
                <div>
                  <Label className="text-gray-400">Time</Label>
                  <Input value={form.time} onChange={e => setForm({ ...form, time: e.target.value })}
                    placeholder="e.g. 8:00 PM MYT" className="bg-gray-800 border-gray-700 text-white mt-1"
                    data-testid="form-time" />
                </div>
                <div>
                  <Label className="text-gray-400">Location / Link</Label>
                  <Input value={form.location_link} onChange={e => setForm({ ...form, location_link: e.target.value })}
                    placeholder="e.g. Zoom / https://..." className="bg-gray-800 border-gray-700 text-white mt-1"
                    data-testid="form-location" />
                </div>
                <div>
                  <Label className="text-gray-400">Category</Label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full mt-1 bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="form-category">
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-400">Type</Label>
                  <select value={form.item_type} onChange={e => setForm({ ...form, item_type: e.target.value })}
                    className="w-full mt-1 bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="form-type">
                    {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-gray-400">Description</Label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description..." rows={3}
                  className="w-full mt-1 bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm resize-none"
                  data-testid="form-description" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={cancelEdit} className="text-gray-400">Cancel</Button>
                <Button onClick={handleSave} disabled={saving || !form.title || !form.date}
                  className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-schedule-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {isNew ? 'Create' : 'Update'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Items List */}
        {filtered.length === 0 ? (
          <Card className="bg-[#141414] border-gray-800">
            <CardContent className="p-10 text-center">
              <Calendar className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 mb-2">No schedule items yet</p>
              <p className="text-gray-600 text-sm">Click "Add Item" to create your first entry</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(item => {
              const cat = getCategoryInfo(item.category);
              return (
                <Card key={item.item_id} className="bg-[#141414] border-gray-800 hover:border-gray-700 transition-colors"
                  data-testid={`schedule-item-${item.item_id}`}>
                  <CardContent className="p-4 flex items-start gap-4">
                    {/* Date badge */}
                    <div className="w-14 h-14 rounded-lg bg-gray-800 flex flex-col items-center justify-center shrink-0">
                      <span className="text-xs text-gray-500 uppercase">
                        {item.date ? new Date(item.date + 'T00:00:00').toLocaleDateString('en', { month: 'short' }) : '—'}
                      </span>
                      <span className="text-lg font-bold text-white">
                        {item.date ? new Date(item.date + 'T00:00:00').getDate() : '—'}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-white font-medium truncate">{item.title}</p>
                        <Badge className={`${cat.color} text-white text-[10px] px-1.5 py-0`}>{cat.label}</Badge>
                        <Badge variant="outline" className="text-gray-500 text-[10px] px-1.5 py-0 border-gray-700">
                          {item.item_type === 'monthly' ? 'Monthly' : 'Yearly'}
                        </Badge>
                      </div>
                      {item.description && <p className="text-sm text-gray-500 truncate">{item.description}</p>}
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-600">
                        {item.time && (
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {item.time}</span>
                        )}
                        {item.location_link && (
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {item.location_link}</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(item)}
                        className="text-gray-500 hover:text-amber-400" data-testid={`edit-${item.item_id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.item_id)}
                        className="text-gray-500 hover:text-red-400" data-testid={`delete-${item.item_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleManager;
